import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

import ErrorHandler from '../../../common/utils/errorHandler';
import { parseApiError } from '../../../utils/parseApiError';
import { LoginFormProps, SignUpFormValues } from './types';
import { validationSchema } from './validationSchema';
// import { RoleSelector } from './RoleSelector/RoleSelector';
import { SignInForm } from './SignInForm/SignInForm';
import { SignUpForm } from './SignUpForm/SignUpForm';
import { FormActions } from './FormActions/FormActions';
import { useLoadingStatus } from '../../../hooks/useLoadingStatus';
import { authService } from '../../../services/auth/AuthService';
import { useUser, useUserActions } from '../../../hooks/useUser';
import { AuthTabs, UserRole } from '../../../common/enums/auth';
import {
  isReactNative,
  sendEventToReactNative,
} from '../../../common/utils/reactNativeHelpers';
import { WebViewEvents } from '../../../common/enums/webViewEvents';
import { StyledForm } from '../../common/StyledForm/StyledForm';
import { signInValidationSchema } from './signInValidationSchema';
import { isPendingUserResponse } from '../../../common/utils/authHelpers';

const BASE_URL = import.meta.env.VITE_APP_BASE_URL;

export const LoginForm: React.FC<LoginFormProps> = ({
  activeTab,
  setIsForgotPassword,
  setActiveTab,
  onAuthOutcome,
}) => {
  const [serverError, setServerError] = useState<string | null>(null);
  const [loadingAuthButton, setLoadingAuthButton] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { stopLoadingButtonStatus } = useLoadingStatus();

  const { t } = useTranslation();
  const { mobileUserAuthError } = useUser();
  const { setUserPhoneOrEmail } = useUserActions();

  const errorHandler = new ErrorHandler(t, {
    validationError: t('errors.invalidEmailConfirmationCode'),
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    if (message) {
      setServerError(decodeURIComponent(message));
    }
  }, [location, navigate]);

  useEffect(() => {
    if (mobileUserAuthError) {
      setServerError(mobileUserAuthError);
    }
  }, [mobileUserAuthError]);

  const onFormSubmit = async (data: SignUpFormValues) => {
    setServerError('');
    try {
      if (activeTab === AuthTabs.SIGN_UP) {
        const filteredData: Partial<SignUpFormValues> = {};

        Object.entries(data).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            filteredData[key as keyof SignUpFormValues] = value;
          }
        });

        try {
          const response = await authService.signUp(filteredData as SignUpFormValues);

          // Handle pending user case
          if (isPendingUserResponse(response)) {
            setUserPhoneOrEmail({ phone: filteredData.phone as string });
            onAuthOutcome({
              kind: 'pending',
              deliveredVia: response.deliveredVia ?? null,
              availableChannels: response.availableChannels ?? [],
            });
            return;
          }

          // Normal sign-up flow
          formik.resetForm();
          setUserPhoneOrEmail({ phone: filteredData.phone as string });
          onAuthOutcome({
            kind: 'signup',
            deliveredVia: response.deliveredVia,
            availableChannels: response.availableChannels,
          });
          return;
        } catch (error) {
          Sentry.captureException({
            message: '[authService][signUp]',
            error,
          });
          const { message } = parseApiError(error);

          // Використовуємо переклади для специфічних помилок
          if (message.includes('phone number is already exist')) {
            setServerError(t('userAuth.validationErrors.phoneAlreadyInUse'));
          } else if (message.includes('email is already exist')) {
            setServerError(t('userAuth.validationErrors.emailAlreadyInUse'));
          } else {
            setServerError(message);
          }
          return;
        }
      } else {
        // Phone-based sign-in flow
        try {
          // Step 1: Initialize phone sign-in (send verification code)
          const delivery = await authService.initPhoneSignIn(data.phone);

          setUserPhoneOrEmail({ phone: data.phone });
          onAuthOutcome({
            kind: 'signin',
            deliveredVia: delivery.deliveredVia,
            availableChannels: delivery.availableChannels,
          });
        } catch (error) {
          Sentry.captureException({
            message: '[authService][initPhoneSignIn]',
            error,
          });
          const parsed = parseApiError(error);
          setServerError(parsed.message);
        }
      }
    } catch (error) {
      const handledErrors = errorHandler.handleError(error);
      Sentry.captureException(error);
      handleError(handledErrors);
    }
  };

  const formik = useFormik<SignUpFormValues>({
    initialValues: {
      email: '',
      password: '',
      repeatPassword: '',
      phone: '',
      checked: false,
      role: UserRole.USER,
    },
    validationSchema:
      activeTab === AuthTabs.SIGN_IN
        ? signInValidationSchema(t)
        : validationSchema(t),
    initialTouched: {
      email: false,
      password: false,
      repeatPassword: false,
      phone: false,
      checked: false,
      role: false,
    },
    onSubmit: onFormSubmit,
  });

  const {
    values,
    // handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    setFieldError,
  } = formik;

  const handleChangePhone = (newValue: string) => {
    setFieldValue('phone', newValue, true);
  };

  const handleError = (errors: Record<string, string>) => {
    if (typeof errors === 'object' && 'server' in errors) {
      setServerError(errors.server);
      return;
    }
    for (const key in errors) {
      if (key === 'password' || key === 'email') {
        const errMessage = errors[key];
        setFieldError(
          key,
          errMessage || t('userAuth.validationErrors.loginInvalidValues'),
        );
      } else if (
        typeof errors[key] === 'object' &&
        Array.isArray(errors[key])
      ) {
        (errors[key] as string[]).forEach((error) => {
          if (error.includes('email')) {
            setFieldError('email', t('userAuth.validationErrors.invalidEmail'));
          }
        });
      }
    }
  };

  const handleGoogleAuth = () => {
    if (isReactNative) {
      setLoadingAuthButton(true);
      sendEventToReactNative(WebViewEvents.GOOGLE_AUTH_EVENT, values.role);
      return;
    }
    window.location.href = `${BASE_URL}/api/auth/google/login?role=${values.role}`;
  };

  const checkDisabledButton = () => {
    if (activeTab === AuthTabs.SIGN_IN) {
      return (
        !values.phone ||
        isSubmitting
      );
    }
    return isSubmitting;
  };

  const isSignIn = activeTab === AuthTabs.SIGN_IN;
  const isSignUp = activeTab === AuthTabs.SIGN_UP;

  return (
    <StyledForm onSubmit={handleSubmit}>
      {/* <RoleSelector value={values.role} onChange={handleChange} /> */}

      {isSignIn ? (
        <SignInForm
          formik={formik}
          handleChangePhone={handleChangePhone}
          handleGoogleAuth={handleGoogleAuth}
          loadingAuthButton={loadingAuthButton}
          stopLoadingButtonStatus={stopLoadingButtonStatus}
          setIsForgotPassword={setIsForgotPassword}
        />
      ) : (
        <SignUpForm
          formik={formik}
          handleChangePhone={handleChangePhone}
          handleGoogleAuth={handleGoogleAuth}
          loadingAuthButton={loadingAuthButton}
          stopLoadingButtonStatus={stopLoadingButtonStatus}
        />
      )}

      <FormActions
        isSubmitting={isSubmitting}
        isSignUp={isSignUp}
        setActiveTab={setActiveTab}
        checkDisabledButton={checkDisabledButton}
        serverError={serverError}
      />
    </StyledForm>
  );
};
